/**
 * 사이트 문안. 서비스가 늘거나 상태가 바뀌면 여기만 고친다.
 *
 * **화면 언어는 영어다 (2026-08-19 사용자 지시 — "cottonray.com 의 언어를 영어로").**
 * 법정 표기(상호·대표자명·주소)만 등록된 한국어 값을 그대로 두고 영문을 병기한다 —
 * 아래 `BUSINESS` 주석 참고.
 *
 * 링크는 **실제로 200이 뜨는 주소만** 넣는다 (2026-08-02 실측).
 * 제품 사이트가 있으면 스토어가 아니라 그쪽으로 보낸다 — 스토어로 보내면 데스크톱
 * 설치 파일(dmg/exe)로 가는 길이 회사 사이트에서 사라진다 (2026-08-09 수정).
 * 사이트가 없는 서비스는 link를 비워 죽은 링크를 만들지 않는다.
 *
 * **상태와 스토어 주소는 콘솔이 아니라 공개 스토어에서 확인한 것만 적는다 (2026-08-24).**
 * 그 전까지 SoundTether·시험보소가 `In development` 로 적혀 있었는데 둘 다 이미 양쪽
 * 스토어에서 팔리고 있었고, 픽모·라이프덱·벨라리아는 아예 목록에 없었다 — 심사 통과와
 * 사이트 갱신이 다른 작업이라 벌어진 일이다. 확인 방법은 두 줄이다:
 * `itunes.apple.com/lookup?id=<앱ID>` 가 1건을 주는가, Play 공개 URL 이 200 인가.
 *
 * 쿠팡 파트너스 자동화 워커는 내부 운영 도구라 여기 넣지 않는다 (제품이 아니다).
 */

export interface Service {
  /** 저장소·서브도메인에서 쓰는 이름 */
  slug: string
  /**
   * 사람이 부르는 이름 — **스토어에 등록한 제품명 그대로** 적는다 (2026-08-10).
   * 한글 이름 서비스(시험보소)는 사이트가 영어가 된 뒤에도 한글 그대로 둔다.
   * 스토어에 그 이름으로 올라가 있어서, 로마자로 바꿔 적으면 사이트가 없는 제품을
   * 가리키게 된다. 슬러그가 필요하면 위 `slug`가 이미 있다.
   */
  name: string
  description: string
  /**
   * `Free` 는 **지금 누구나 쓸 수 있지만 돈을 받지 않는 것**이다 (2026-08-12 retype 공개).
   * `In development`(아직 못 씀)과도, `Paid`(돈을 받음)와도 사실이 다르다 — 셋을 뭉치면
   * 사이트가 방문자에게 거짓말을 한다.
   */
  status: 'Paid' | 'Free' | 'In development'
  /** 제품 사이트·게임 페이지처럼 스토어보다 먼저 보내고 싶은 곳. 카드의 첫 버튼이 된다 */
  link?: { href: string; label: string }
  /**
   * 공개 스토어 주소. **공개된 것만 적는다** — 심사 대기·반려 중인 플랫폼은 비워 둔다
   * (Easy Air Screen Lite 의 iOS 판이 그렇다). 같은 앱이 두 스토어에 다 있으면 둘 다
   * 보여 준다: 한쪽만 걸면 나머지 플랫폼 방문자는 사이트에서 받을 길이 없다.
   */
  stores?: { ios?: string; android?: string }
  macPurchase?: { href: string; price: string }
}

export const SERVICES: readonly Service[] = [
  {
    slug: 'easy-air-screen',
    name: 'Easy Air Screen',
    description:
      'Use a tablet as a second monitor for your Mac. Put both on the same Wi-Fi and extend or mirror the screen with no cable.',
    status: 'Paid',
    link: {
      href: 'https://easy-air-screen.cottonray.com',
      label: 'Learn more',
    },
    stores: {
      ios: 'https://apps.apple.com/app/id6757753606',
      android: 'https://play.google.com/store/apps/details?id=com.somnal.app.easy.air.screen',
    },
  },
  {
    slug: 'easy-air-screen-lite',
    name: 'Easy Air Screen Lite',
    description:
      'The same wireless second screen, free with ads instead of a purchase. It pairs with the same Mac app.',
    status: 'Free',
    // 아이폰·아이패드 판은 아직 심사를 통과하지 못했다 — 통과하면 ios 를 여기 넣는다
    stores: {
      android: 'https://play.google.com/store/apps/details?id=org.somnal.app.easyairscreen.lite',
    },
  },
  {
    slug: 'soundtether',
    name: 'SoundTether',
    description:
      'Hear your PC audio through your phone and earbuds. Audio is never stored on a server — it travels straight between the two devices.',
    status: 'Free',
    link: { href: 'https://soundtether.cottonray.com', label: 'Learn more' },
    stores: {
      ios: 'https://apps.apple.com/app/id6797515064',
      android: 'https://play.google.com/store/apps/details?id=org.somnal.app.soundtether',
    },
  },
  {
    slug: 'peekmo',
    name: 'Peekmo',
    description:
      'Notes you actually see again. A few lines you cannot afford to forget sit on your lock screen and home screen widgets — no folders, no tags, nothing to file.',
    status: 'Free',
    stores: {
      ios: 'https://apps.apple.com/app/id6800045634',
      android: 'https://play.google.com/store/apps/details?id=org.somnal.app.peekmo',
    },
  },
  {
    slug: 'velaria',
    name: '벨라리아',
    description:
      'Wedding spending on a single sheet. Nine preset groups cover what a Korean wedding actually costs, so you start from a filled-in list instead of a blank one.',
    status: 'Free',
    stores: {
      ios: 'https://apps.apple.com/app/id6800781056',
      android: 'https://play.google.com/store/apps/details?id=org.somnal.app.velaria',
    },
  },
  {
    slug: 'soulweld',
    name: 'Soulweld',
    description:
      'Fight through a pixel world, collect equipment, and grow into one of six classes. An action roguelike RPG built around surviving the next wave.',
    status: 'Free',
    stores: {
      android: 'https://play.google.com/store/apps/details?id=com.cottonray.app.soulweld&utm_source=cottonray&utm_campaign=games_focus_202609',
    },
  },
]

/**
 * 사업자 정보 — **실제 등록 정보다. 값을 추측으로 고치지 말 것.**
 *
 * 전자상거래법 제10조가 사이버몰 운영자에게 상호·대표자명·사업자등록번호 표시를 요구한다.
 * 사용자 대면 사이트에는 이 표기가 들어가야 한다 (docs/rules.md).
 *
 * 사업자등록번호는 체크섬(가중치 1,3,7 반복 + 아홉째 자리 보정)을 만족함을 확인했다 (2026-08-03).
 */
/**
 * **브랜드 이름.** 화면에 보이는 이름은 전부 이것이다 — 히어로·제목·본문·`<title>`.
 *
 * 아래 `BUSINESS.name`(`솜날`)과 **일부러 다르다.** 2026-08-17 에 사명이 Cottonray 로
 * 바뀌었지만 사업자등록증의 상호는 아직 `솜날`이고, 전자상거래법 제10조가 사이버몰에
 * 요구하는 것은 **등록된 상호**다. 등록을 정정하지 않은 채 푸터 표기까지 바꾸면 그 표기가
 * 거짓이 된다. 그래서 **보이는 브랜드는 Cottonray, 법정 표기는 솜날**로 갈라 둔다.
 *
 * 세무서에 상호 정정신고를 하면 그때 `BUSINESS.name` 도 바꾸고 이 주석을 지운다.
 */
/**
 * 로그인 없이 쓰는 무료 웹 도구. **홈에서 반드시 링크한다** — sitemap 에만 있고 내부 링크가
 * 없으면 구글이 고아 페이지로 보고 크롤 우선순위를 낮춘다(2026-08-25 실측: 도구 5개가
 * 그 상태였다). 교차 유입도 여기서 생긴다 — 견적서를 찾아 온 사람이 완료보고서를 본다.
 *
 * 문구는 한국어다. 이 도구들의 검색 수요가 한국어 실무 검색어이기 때문이고, 게임·앱의
 * 영어 단일 규칙(rules.md §전략)과는 다른 대상이다.
 */
export interface WebTool {
  href: string
  name: string
  description: string
}

export const WEB_TOOLS: readonly WebTool[] = [
  {
    href: '/quote-builder',
    name: '견적서 만들기',
    description: '품목과 금액만 넣으면 부가세까지 계산된 견적서가 나옵니다.',
  },
  {
    href: '/statement-builder',
    name: '거래명세서 만들기',
    description: '납품한 품목과 금액으로 거래명세서를 바로 만듭니다.',
  },
  {
    href: '/completion-report',
    name: '작업완료보고서 만들기',
    description: '한 일을 적으면 고객에게 낼 완료보고서가 나옵니다.',
  },
  {
    href: '/receipt-builder',
    name: '간이영수증 만들기',
    description: '받은 금액과 내역으로 영수증을 만듭니다.',
  },
  {
    href: '/resignation-timeline',
    name: '퇴사 타임라인',
    description: '퇴사일만 넣으면 실업급여·건강보험·퇴직금 마감이 D-day 로 깔립니다.',
  },
]

export const BRAND = 'Cottonray'

export const BUSINESS = {
  /**
   * **법정 상호다. 화면 문구에 쓰지 마라 — 그 자리는 위 `BRAND` 다.**
   *
   * 사이트가 영어가 된 뒤에도 이 값은 한국어 그대로다. 등록된 상호가 한국어이고
   * 제10조가 요구하는 것은 **등록된 표기**라, 로마자만 적으면 그 표기가 등록과 어긋난다.
   * 읽는 사람을 위해 아래 `nameRoman` 을 함께 보여 주되 괄호 안 원문을 지우지 않는다.
   */
  name: '솜날',
  /** 병기용 로마자. 법정 표기가 아니다 — 등록 상호는 위 `name` 하나뿐이다 */
  nameRoman: 'Somnal',
  representative: '최정현',
  /** 여권 표기 CHOI JEONG HYEON 을 문장체로 적은 것. 법정 표기는 위 `representative` 다 */
  representativeRoman: 'Choi Jeong-hyeon',
  registrationNumber: '215-26-51488',
  /**
   * 사업장 주소. 개인정보처리방침의 **서면(우편) 권리 행사 접수처**로 쓴다 —
   * 「개인정보 보호법」 시행령 제41조 제1항이 서면 접수 경로를 전제한다.
   * 전자상거래법 제10조도 사이버몰에 주소 표시를 요구하므로 사이트 푸터에도 넣을 수 있다.
   *
   * **한국어 원문을 지우지 않는다** — 국내 우편이 이 표기로 배달된다.
   */
  address: '광주광역시 서구 월산로165번길 5',
  /** 국외에서 부칠 사람을 위한 도로명주소 영문 표기. 위 한국어 표기와 같은 곳이다 */
  addressRoman: '5, Wolsan-ro 165beon-gil, Seo-gu, Gwangju, Republic of Korea',
} as const

/**
 * **정보주체 권리 행사 접수 창구** — 열람·정정·삭제·처리정지·전송 요구. 개인정보처리방침
 * 아홉 문서가 이 주소 하나만 쓴다. 같은 요청에 두 주소가 보이면 안 되기 때문이다.
 *
 * 일반 문의(사용법·불편·제휴)는 아래 `SUPPORT_EMAIL` 이다 — 2026-08-07 에 목적별로 갈랐다.
 *
 * 이 주소는 한동안 메일을 받을 수 없어서(MX 없음) 임시로 개인 메일함을 적어 뒀다.
 * 2026-08-03 Cloudflare Email Routing이 붙어 되돌렸다 — MX 3건(route1/2/3.mx.cloudflare.net)
 * + SPF + DKIM 확인, 같은 날 실제 배달까지 사용자 확인. 2026-08-07 재확인.
 * 설정·복구 절차는 infra/docs/email-routing.md.
 */
export const CONTACT_EMAIL = 'privacy@cottonray.com'

/**
 * 일반 문의 창구. **앱의 "지원·문의"가 도착하는 주소다** — 네 앱 모두 설정 화면에서
 * `cottonray.com/#contact` 로 보내고, 그 자리에 이 주소가 있다 (2026-08-07 지시).
 *
 * **CONTACT_EMAIL 과 목적이 다르다.** 개인정보 열람·삭제·처리정지 같은 정보주체 권리 행사는
 * 방침에 적힌 `privacy@` 하나로만 받는다 — 같은 요청에 두 주소가 보이면 이용자가 어디로
 * 보내야 할지 모르고, 한쪽을 놓치면 법정 기한을 넘긴다. 여기는 사용법·불편·제휴처럼
 * 권리 행사가 아닌 문의만 받는다. (`App.test.tsx` 가 이 분리를 회귀로 잡는다)
 *
 * **실제 수신 확인 완료 (2026-08-07, 사용자).** 외부 계정에서 보낸 메일이 목적지 메일함에
 * 도착하는 것을 사람이 직접 확인했다 — 포트 25 가 로컬·서버 양쪽에서 막혀 있어 자동 검증은
 * 불가능하다. 주소를 바꾸면 같은 방식으로 다시 확인할 것: 2026-08-03 에 `privacy@` 가
 * MX 없이 방침 네 곳에 적혀 **보낸 메일이 전부 사라지고 있던** 적이 있다.
 */
export const SUPPORT_EMAIL = 'help@cottonray.com'

/**
 * 개인정보 보호책임자 직통. 「개인정보 보호법」 제31조의 보호책임자 연락처이자
 * 제34조 유출 통지·신고와 관련한 연락 경로다.
 *
 * **위 CONTACT_EMAIL 과 역할이 다르다** — privacy@ 는 이용자가 권리를 행사하는 접수 창구고,
 * 여기는 책임자 본인에게 닿는 경로다. 같은 사람이 둘 다 받지만, 방침에 적히는 자리가 달라
 * 주소를 나눠 둔다 (열람 요구가 어디로 가야 하는지는 여전히 하나뿐이다).
 *
 * **수신 배선 완료 (2026-08-06, docs/requests/completed/18).** Cloudflare Email Routing 에
 * `security@cottonray.com → somnal.kor@gmail.com` 규칙이 Active 로 등록됐다 (privacy@ 와 같은
 * 목적지, MX·SPF·DKIM 은 2026-08-03 부터 있다).
 *
 * **실제 수신 확인 완료 (2026-08-07, 사용자).** 외부 계정에서 보낸 메일이 목적지 메일함에
 * 도착하는 것을 사람이 직접 확인했다 — 포트 25 가 로컬 ISP 와 OCI 서버 양쪽에서 막혀 있어
 * 자동 검증은 불가능하다. 주소를 바꾸면 같은 방식으로 다시 확인할 것.
 */
export const PRIVACY_OFFICER_EMAIL = 'security@cottonray.com'

/** 운영 방식 — 검증 가능한 사실만 적는다. 다짐이나 포부는 넣지 않는다 */
export const PRINCIPLES: readonly { title: string; body: string }[] = [
  {
    title: 'We keep running what we build',
    body: 'The person who wrote the app also handles its releases, monitoring, and backups.',
  },
  {
    title: 'We ask only for what we need',
    body: 'Anything that works without an account never asks you to make one. What each service handles, and why, is set out in its own privacy policy.',
  },
  {
    title: 'We build small and revise often',
    body: 'Rather than shipping a long feature list up front, we build what is needed, use it ourselves, and fix what gets in the way.',
  },
]
